import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { X } from "lucide-react";
import { Link } from "react-router-dom";

import { SearchableSelect, type SearchableSelectOption } from "@/components/ui/SearchableSelect";
import { qcIncidentPath } from "@/lib/qc/qcIncidentNav";
import { cn } from "@/lib/utils";

import {
  fetchQualityIncidentQuickSearch,
  type QualityIncidentSearchRow,
} from "./casesApi";

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(timer);
  }, [delayMs, value]);
  return debounced;
}

function incidentChipLabel(row: Pick<QualityIncidentSearchRow, "id" | "pcbaSerial" | "deviceLabelSerial">): string {
  const serial = row.deviceLabelSerial?.trim() || row.pcbaSerial?.trim();
  return serial ? `#${row.id} · ${serial}` : `#${row.id}`;
}

function toSearchOption(row: QualityIncidentSearchRow): SearchableSelectOption {
  return {
    value: String(row.id),
    label: incidentChipLabel(row),
    description: [row.issue?.trim(), row.type?.trim(), row.mcuUuid?.trim()].filter(Boolean).join(" · ") || null,
  };
}

export function QualityIncidentPicker({
  id,
  values,
  knownLabels,
  disabled,
  onChange,
}: {
  id: string;
  values: number[];
  knownLabels?: Record<number, string | null | undefined>;
  disabled?: boolean;
  onChange: (incidentIds: number[]) => void;
}): JSX.Element {
  const [searchValue, setSearchValue] = useState("");
  const [knownRows, setKnownRows] = useState<Map<number, QualityIncidentSearchRow>>(new Map());
  const debouncedSearch = useDebouncedValue(searchValue.trim(), 250);
  const lookupQuery = values[0] != null ? String(values[0]) : "";
  const searchQuery = debouncedSearch.length >= 1 ? debouncedSearch : lookupQuery;

  const searchResultsQuery = useQuery({
    queryKey: ["quality", "incident-quick-search", searchQuery],
    queryFn: () => fetchQualityIncidentQuickSearch(searchQuery),
    enabled: searchQuery.length >= 2 || /^\d+$/.test(searchQuery),
    staleTime: 20_000,
  });

  useEffect(() => {
    const rows = searchResultsQuery.data?.rows ?? [];
    if (rows.length === 0) {
      return;
    }
    setKnownRows((current) => {
      const next = new Map(current);
      for (const row of rows) {
        next.set(row.id, row);
      }
      return next;
    });
  }, [searchResultsQuery.data?.rows]);

  const selectedRows = values.map((incidentId) => ({
    id: incidentId,
    chipLabel:
      knownRows.get(incidentId) != null
        ? incidentChipLabel(knownRows.get(incidentId)!)
        : knownLabels?.[incidentId]?.trim() || `#${incidentId}`,
  }));

  const options = useMemo(() => {
    const rows = searchResultsQuery.data?.rows ?? [];
    const byId = new Map<number, QualityIncidentSearchRow>();
    for (const row of rows) {
      byId.set(row.id, row);
    }
    return [...byId.values()]
      .filter((row) => !values.includes(row.id))
      .map(toSearchOption);
  }, [searchResultsQuery.data?.rows, values]);

  return (
    <div className="space-y-1.5">
      {selectedRows.length > 0 ? (
        <div className="flex flex-wrap items-center gap-1.5">
          {selectedRows.map((selected) => (
            <span
              key={selected.id}
              className="inline-flex max-w-full items-center gap-1 rounded-md bg-slate-100 px-2 py-0.5 text-xs text-slate-800"
            >
              <Link className="text-xs text-sky-700 hover:underline" to={qcIncidentPath(selected.id, "quality")}>
                {selected.chipLabel}
              </Link>
              <button
                type="button"
                className="rounded p-0.5 text-slate-500 hover:bg-slate-200 hover:text-slate-800 disabled:pointer-events-none"
                aria-label={`Remove ${selected.chipLabel}`}
                disabled={disabled}
                onClick={() => onChange(values.filter((incidentId) => incidentId !== selected.id))}
              >
                <X className="h-3 w-3" aria-hidden />
              </button>
            </span>
          ))}
        </div>
      ) : null}
      <SearchableSelect
        id={id}
        value=""
        onValueChange={(option) => {
          if (option == null) {
            return;
          }
          const parsed = Number(option.value);
          if (!Number.isFinite(parsed) || parsed < 1 || values.includes(parsed)) {
            return;
          }
          onChange([...values, parsed]);
          setSearchValue("");
        }}
        options={options}
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        placeholder="Search incident id, PCBA serial, device serial, or MCU UUID"
        searchPlaceholder="Incident id, PCBA, device serial, or MCU UUID"
        emptyMessage={
          searchValue.trim().length < 2 && !/^\d+$/.test(searchValue.trim())
            ? "Type an incident id or at least two characters of a serial."
            : "No QC incidents match."
        }
        isLoading={searchResultsQuery.isFetching}
        disabled={disabled}
        panelMinWidth={360}
        className={cn(values.length > 0 ? "mt-0" : undefined)}
      />
    </div>
  );
}
